import React, { useMemo, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import {
  addWeeks,
  endOfWeek,
  isSameWeek,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import {
  PurchaseOrderItem,
  PurchaseOrderItemStatus,
} from '@domain/purchasing';
import { usePurchaseOrderItems } from '@features/purchasing/hooks';
import PurchaseOrderDetails from '@features/purchasing/components/details/PurchaseOrderDetails';
import { PurchaseOrderShipment } from '@features/purchasing/components/shipment/PurchaseOrderShipment';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { useGenericDrawer } from '@shared/hooks';
import { APP_CONFIG } from '../../../../config/app.config';
import PurchaseOrderWeekStrip, {
  PurchaseOrderWeekStripHandle,
} from './PurchaseOrderWeekStrip';
import PurchaseOrderItemList from './PurchaseOrderItemTable';
import PurchaseOrderOverdueDialog from './PurchaseOrderOverdueDialog';
import UpdatePurchaseOrderItemDates from './UpdatePurchaseOrderItemDates';
import {
  StatusMilestone,
  WEEKS_AFTER,
  WEEKS_BEFORE,
  dayKey,
  getDisplayMilestone,
  getLatestOverdueMilestone,
  getStatusMilestones,
  isItemOverdue,
  milestoneInWeek,
  statusForMilestone,
} from './utils';
import DashboardCardHeader from '../general/DashboardCardHeader';
import { Icon, IconSize } from '@shared/icons';

const DashboardIncomingShipments: React.FC = () => {
  const colors = APP_CONFIG.ui.theme.colors;
  const { openDrawer } = useGenericDrawer();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const {
    purchaseOrderItems,
    isLoadingItems,
    updatePurchaseOrderItemStatus,
  } = usePurchaseOrderItems();
  const weekStripRef = useRef<PurchaseOrderWeekStripHandle>(null);

  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [overdueOpen, setOverdueOpen] = useState(false);
  const [overdueDialogIds, setOverdueDialogIds] = useState<string[]>([]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const currentWeekStart = useMemo(
    () => startOfWeek(today, { weekStartsOn: 1 }),
    [today]
  );
  const [selectedWeekStart, setSelectedWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  const selectedWeekEnd = useMemo(
    () => endOfWeek(selectedWeekStart, { weekStartsOn: 1 }),
    [selectedWeekStart]
  );

  const calendarStart = useMemo(
    () => addWeeks(selectedWeekStart, -(WEEKS_BEFORE + WEEKS_AFTER)),
    [selectedWeekStart]
  );
  const calendarEnd = useMemo(
    () =>
      endOfWeek(addWeeks(selectedWeekStart, WEEKS_BEFORE + WEEKS_AFTER), {
        weekStartsOn: 1,
      }),
    [selectedWeekStart]
  );

  const eligibleItems = useMemo(() => {
    if (!purchaseOrderItems) return [];
    return purchaseOrderItems.filter((item) => {
      if (item.purchaseOrder?.draft) return false;
      return getStatusMilestones(item).length > 0;
    });
  }, [purchaseOrderItems]);

  const itemsByDay = useMemo(() => {
    const map = new Map<string, PurchaseOrderItem[]>();
    for (const item of eligibleItems) {
      for (const milestone of getStatusMilestones(item)) {
        const day = milestone.date;
        if (day < calendarStart || day > calendarEnd) continue;
        const key = dayKey(day);
        const list = map.get(key) ?? [];
        if (!list.includes(item)) list.push(item);
        map.set(key, list);
      }
    }
    return map;
  }, [eligibleItems, calendarStart, calendarEnd]);

  const overdueItems = useMemo(() => {
    return eligibleItems
      .filter((item) => isItemOverdue(item, today))
      .sort((a, b) => {
        const dateA =
          getLatestOverdueMilestone(a, today)?.date.getTime() ??
          Number.POSITIVE_INFINITY;
        const dateB =
          getLatestOverdueMilestone(b, today)?.date.getTime() ??
          Number.POSITIVE_INFINITY;
        return dateA - dateB;
      });
  }, [eligibleItems, today]);

  const overdueDialogItems = useMemo(() => {
    if (!overdueOpen || overdueDialogIds.length === 0) return [];
    const byId = new Map(
      eligibleItems.filter((item) => item.id).map((item) => [item.id!, item])
    );
    return overdueDialogIds
      .map((id) => byId.get(id))
      .filter((item): item is PurchaseOrderItem => Boolean(item));
  }, [overdueOpen, overdueDialogIds, eligibleItems]);

  const weekListItems = useMemo(() => {
    return eligibleItems
      .filter((item) =>
        getStatusMilestones(item).some((milestone) =>
          milestoneInWeek(milestone, selectedWeekStart, selectedWeekEnd)
        )
      )
      .sort((a, b) => {
        const dateA =
          getDisplayMilestone(
            a,
            selectedWeekStart,
            selectedWeekEnd
          )?.date.getTime() ?? Number.POSITIVE_INFINITY;
        const dateB =
          getDisplayMilestone(
            b,
            selectedWeekStart,
            selectedWeekEnd
          )?.date.getTime() ?? Number.POSITIVE_INFINITY;
        return dateA - dateB;
      });
  }, [eligibleItems, selectedWeekStart, selectedWeekEnd]);

  const handleSelectWeek = (weekStart: Date) => {
    if (!isSameWeek(weekStart, selectedWeekStart, { weekStartsOn: 1 })) {
      setPage(1);
      setSelectedWeekStart(weekStart);
    }
  };

  const openOverdueDialog = () => {
    setOverdueDialogIds(
      overdueItems
        .map((item) => item.id)
        .filter((id): id is string => Boolean(id))
    );
    setOverdueOpen(true);
  };

  const closeOverdueDialog = () => {
    setOverdueOpen(false);
    setOverdueDialogIds([]);
  };

  const handleDelayed = (item: PurchaseOrderItem) => {
    openBottomSheet({
      title: 'Update dates',
      component: (
        <UpdatePurchaseOrderItemDates
          item={item}
          onClose={closeBottomSheet}
        />
      ),
    });
  };

  const handleAddShipment = (item: PurchaseOrderItem) => {
    if (!item.purchaseOrder) return;
    openBottomSheet({
      title: 'Add Shipment',
      component: (
        <PurchaseOrderShipment
          handleClose={closeBottomSheet}
          handleFinish={() => closeBottomSheet()}
          purchaseOrder={item.purchaseOrder}
          selectedShipment={item.shipment || undefined}
          purchaseOrderItems={[item]}
          products={item.product ? [item.product] : []}
        />
      ),
    });
  };

  const openPurchaseOrder = (item: PurchaseOrderItem) => {
    const purchaseOrderId = item.purchaseOrder?.id;
    if (!purchaseOrderId) return;
    openDrawer(
      `Purchase Order ${item.purchaseOrder?.poNumber || 'Details'}`,
      <PurchaseOrderDetails purchaseOrderId={purchaseOrderId} />
    );
  };

  const handleConfirm = async (
    item: PurchaseOrderItem,
    milestone: StatusMilestone
  ) => {
    if (!item.id) return;
    const status = statusForMilestone(milestone.kind);
    if (item.status === status) return;

    setUpdatingId(item.id);
    try {
      await updatePurchaseOrderItemStatus({ itemId: item.id, status });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleChangeStatus = async (
    item: PurchaseOrderItem,
    status: PurchaseOrderItemStatus
  ) => {
    if (!item.id || item.status === status) return;

    setUpdatingId(item.id);
    try {
      await updatePurchaseOrderItemStatus({ itemId: item.id, status });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <DashboardCardHeader title="Purchase orders">
        {!isSameWeek(selectedWeekStart, currentWeekStart, {
          weekStartsOn: 1,
        }) && (
            <Button
              endIcon={<Icon icon="ClockBackward" size={IconSize.SMALL} />}
              size="small"
              variant="text"
              color="info"
              onClick={() =>
                weekStripRef.current?.selectWeek(currentWeekStart)
              }
            >
              This week
            </Button>
          )}
        {overdueItems.length > 0 && (
          <Button
            size="small"
            variant="text"
            color="error"
            startIcon={<Icon icon="AlertTriangle" size={IconSize.SMALL} />}
            onClick={openOverdueDialog}
          >
            {overdueItems.length} overdue
          </Button>
        )}
      </DashboardCardHeader>

      <PurchaseOrderWeekStrip
        ref={weekStripRef}
        selectedWeekStart={selectedWeekStart}
        currentWeekStart={currentWeekStart}
        today={today}
        itemsByDay={itemsByDay}
        onSelectWeek={handleSelectWeek}
      />

      <PurchaseOrderItemList
        items={weekListItems}
        page={page}
        isLoading={isLoadingItems}
        today={today}
        selectedWeekStart={selectedWeekStart}
        selectedWeekEnd={selectedWeekEnd}
        currentWeekStart={currentWeekStart}
        updatingId={updatingId}
        onPageChange={setPage}
        onOpen={openPurchaseOrder}
        onConfirm={handleConfirm}
        onDelayed={handleDelayed}
        onAddShipment={handleAddShipment}
        onChangeStatus={handleChangeStatus}
      />

      <PurchaseOrderOverdueDialog
        open={overdueOpen}
        items={overdueDialogItems}
        today={today}
        updatingId={updatingId}
        onClose={closeOverdueDialog}
        onOpen={openPurchaseOrder}
        onConfirm={handleConfirm}
        onDelayed={handleDelayed}
        onAddShipment={handleAddShipment}
        onChangeStatus={handleChangeStatus}
      />
    </Box>
  );
};

export default DashboardIncomingShipments;
